import { ApolloClient, InMemoryCache, gql, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import About from './About';
import Account from './Account';
import './App.css';
import Chat from './Chat';
import ErrorBoundary from './ErrorBoundary';
import Home from "./Home";
import Images from './Images';
import SignIn from './SignIn';
import SignInGetToken from "./SignInGetToken";
import SignInToken from "./SignInToken";
import Places from './Places';
import Purchase from './Purchase';
import Register from './Register';
import Report from "./Report";
import ReportEdit from './ReportEdit';
import Reports from './Reports';
import Travel from "./Travel";
import TravelDetails from "./TravelDetails";
import TravelEdit from './TravelEdit';
import Travels from './Travels';
import NotificationDisplay from './NotificationDisplay';
import UploadMedia from './UploadMedia';
import AboutPrivacy from './AboutPrivacy';
import AboutTermsOfService from './AboutTermsOfService';
import AppInfo from './AppInfo';
import { AppBar, Avatar, Box, Button, Container, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Navbar from './Navbar';
import Hideable from './Hideable';
import Location from './Location';

const ACCOUNT = gql`
query Account {
  account {
    id
    name
    purchase {
      status
    }
    roles
    pictureUrl
  }
}`;

const LOGOUT = gql`
mutation Logout {
  logout
}`;


const client = new ApolloClient({
  uri: process.env.REACT_APP_API_URI,
  // link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  credentials: 'include',
  headers: {
    'x-api-key': process.env.REACT_APP_API_KEY
  }
});

const App = props => {
  const [searchParams] = useSearchParams();
  const { loading, data } = useQuery(ACCOUNT, { client: client });
  const account = data?.account;
  const [notifications, setNotifications] = useState([])
  const location = useLocation();
  const [gpsEnabled, setGpsEnabled] = useState(Location.isGpsEnabled())

  const pSetGpsEnabled = e => {
    //If enabling, verify that GPS is supported
    if (e) {
      Location.getGPSLocation(
        position => {
          Location.setGpsEnabled(e)
          setGpsEnabled(e)
        },
        error => props.onError?.(error)
      )
    } else {
      Location.setGpsEnabled(false)
      setGpsEnabled(false)
    }
  }

  const onError = e => setNotifications([...[e], ...notifications])

  console.log(`App version: ${process.env.REACT_APP_VERSION}`)

  AppInfo.setProfile(searchParams.get('app.profile'))

  if (loading) {
    return null
  }

  return (<ErrorBoundary>
    <div className='presentation'>
      <NotificationDisplay
        notifications={notifications}
        onClose={() => setNotifications([])}
        />
      <TopMenu key={account?.id} account={account} client={client} onError={onError} gpsEnabled={gpsEnabled} setGpsEnabled={pSetGpsEnabled} />
      <div style={{ padding: '1em' }} className='main' id='main'>
        <Routes>
          <Route path='/about' element={<About client={client} />} />
          <Route path='/about/privacy' element={<AboutPrivacy client={client} />} />
          <Route path='/about/terms-of-service' element={<AboutTermsOfService client={client} />} />
          <Route path='/account' element={<Account client={client} />} />
          <Route path='/account/purchase' element={<Purchase client={client} />} />
          <Route path='/chat' element={<Chat client={client} account={account} />} />
          <Route path='/local' element={<Places key={location.state?.key || 'default'} account={account} client={client} onError={onError} />} />
          <Route path='/record' element={<Reports account={account} client={client} />} />
          <Route path='/record/add' element={<ReportEdit client={client} account={account} />} />
          <Route path='/record/edit/:id' element={<ReportEdit client={client} />} />
          <Route path='/record/:id' element={<Report account={account} client={client} />} />
          <Route path='/drive' element={<Travels client={client} />} />
          <Route path='/drive/new' element={<TravelEdit account={account} client={client} />} />
          <Route path='/drive/edit/:id' element={<TravelEdit account={account} client={client} />} />
          <Route path='/drive/:id' element={<Travel account={account} client={client} onError={onError} />} />
          <Route path='/drive/:id/details' element={<TravelDetails client={client} />} />
          <Route path='/gallery' element={<Images account={account} client={client} />} />
          <Route path='/gallery/upload' element={<UploadMedia account={account} client={client} />} />
          <Route path='/sign-in' element={<SignIn client={client} account={account} />} />
          <Route path='/sign-in/get-token' element={<SignInGetToken client={client} />} />
          <Route path='/sign-in/token' element={<SignInToken client={client} />} />
          <Route path='/sign-up' element={<Register account={account} client={client} />} />
          <Route path='/' element={<Home account={account} client={client} gpsEnabled={gpsEnabled} setGpsEnabled={pSetGpsEnabled} />} />
        </Routes>
      </div>
    </div>
  </ErrorBoundary>)
}


const TopMenu = props => {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const pages = [
    {label:'Home', link: '/'},
    {label:'Drive', link: '/drive'},
    {label:'Chat', link: '/chat'},
    {label:'Local', link: '/local'}
  ];
  const anon_settings = [
    {label: 'Sign In', onClick: () => navigate('/sign-in')}
  ];
  const user_settings = [
    {label: 'Account', onClick: () => navigate('/account')},
    {label: 'Sign Out', onClick: () => logout(props.client, navigate)},
  ];
  const settings = props.account ? user_settings : anon_settings;

  const navigate = useNavigate();

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            navscout
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {pages.map(page => (
                <MenuItem key={page.label} onClick={() => {
                  handleCloseNavMenu();
                  navigate(page.link, { state: {key: Date.now()} })
                }}>
                  {page.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.filter(page => page.link !== "/").map((page) => (
              <Button
                key={page.label}
                onClick={() => {handleCloseNavMenu();navigate(page.link)}}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                {page.label}
              </Button>
            ))}
          </Box>
          <Hideable hidden={window.location.pathname === '/'}>
            <Navbar client={props.client} account={props.account} onError={props.onError} size='medium' gpsEnabled={props.gpsEnabled} setGpsEnabled={props.setGpsEnabled} />
          </Hideable>
          <Box sx={{ flexGrow: 0,paddingLeft:'1em' }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt={props.account?.name} src={props.account?.pictureUrl} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem key={setting.label} onClick={() => {handleCloseUserMenu();setting.onClick?.()}}>
                  <Typography sx={{ textAlign: 'center' }}>{setting.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

const logout = (client, navigate) => {
  client.mutate({
    mutation: LOGOUT,
    refetchQueries: ['Account', 'Alerts', 'FindPlace', 'GetCalendar', 'Places']
  }).then(data => {
    if (data.data.register === null) {
      console.log('Sign out failed');
    } else {
      console.log('Signed out');
      navigate('/sign-in');
    }
  });
}

export default App;
